import { useState, useMemo } from 'react';
import { Box, Typography, Switch, Dialog, DialogContent, Stack, useTheme, Skeleton } from '@mui/material';
import Button from './Button';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetAgent, useGetAgentStatus, useUpdateAgentMode } from '@/apis/business/agent/hook';
import { useGetCurrentUser } from '@/apis/user/hook';
import { RxCross1 } from 'react-icons/rx';
import ConfigureActionIcon from '@/assets/icons/ConfigureActionIcon';
import ModeToggleSettingIcon from '@/assets/icons/ModeToggleSettingIcon';
import WarningIcon from '@/assets/icons/WarningIcon';

interface LiveModeToggleBoxProps {
  isCollapsed?: boolean;
}

const MODE_CONFIG = {
  sandbox: {
    bg: '#FFFBEB',
    border: '#FFEFAD',
    text: '#D97706'
  },
  live: {
    bg: '#F8F9FA',
    border: '#ECECEC',
    text: '#09090AB2'
  }
} as const;

function LiveModeToggleSkeleton({ isCollapsed }: { isCollapsed?: boolean }) {
  return (
    <Box px={2} pb={2}>
      <Box
        sx={{
          borderRadius: 2,
          p: 1,
          border: '1px solid #ECECEC'
        }}
      >
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Skeleton variant='text' width={120} height={24} />
          <Skeleton variant='rounded' width={38} height={22} />
        </Stack>

        {!isCollapsed && <Skeleton variant='text' width={180} height={16} />}
      </Box>
    </Box>
  );
}

export default function LiveModeToggleBox({ isCollapsed = false }: LiveModeToggleBoxProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { locationId, agentId } = useParams();

  const { data: currentUser, isLoading: isCurrentUserLoading } = useGetCurrentUser();
  const { data: agent, isLoading } = useGetAgent();
  const { data: agentStatus, refetch } = useGetAgentStatus();
  const { mutateAsync: updateAgentMode, isPending } = useUpdateAgentMode();

  const isAgencyUser = ['agency_admin', 'agency_admin_readonly', 'agent_builder'].includes(currentUser?.role ?? '');

  const [openModal, setOpenModal] = useState(false);
  const [targetMode, setTargetMode] = useState<'sandbox' | 'live' | null>(null);

  const currentMode = agent?.mode ?? 'live';
  const isSandbox = currentMode === 'sandbox';
  const styles = MODE_CONFIG[currentMode];

  const hasConfigIssues = useMemo(() => !agentStatus?.status, [agentStatus]);

  const handleToggle = async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    await refetch();

    const nextMode = checked ? 'sandbox' : 'live';

    setTargetMode(nextMode);
    setOpenModal(true);
  };

  const handleConfirmSwitch = async () => {
    if (!agent?.id || !targetMode) return;

    await updateAgentMode({
      id: agent.id,
      mode: targetMode
    });

    setOpenModal(false);
  };

  const handleConfigureNow = () => {
    navigate(`/location/${locationId}/agent/${agentId}/configure/identity`);
    setOpenModal(false);
  };

  if (isLoading || isCurrentUserLoading || !agent) {
    return <LiveModeToggleSkeleton isCollapsed={isCollapsed} />;
  }

  if (!isAgencyUser) {
    return null;
  }

  return (
    <>
      <Box px={2} pb={2}>
        <Box
          sx={{
            backgroundColor: styles.bg,
            border: `1px solid ${styles.border}`,
            borderRadius: 2,
            p: 1,
            transition: 'all .25s ease'
          }}
        >
          {isCollapsed ? (
            <Stack alignItems='center'>
              <Switch
                checked={isSandbox}
                onChange={handleToggle}
                size='small'
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: styles.text
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: styles.text
                  }
                }}
              />
            </Stack>
          ) : (
            <Stack>
              <Stack direction='row' alignItems='center' justifyContent='space-between'>
                <Typography color={styles.text} fontSize={14} fontWeight={600}>
                  Sandbox Mode
                </Typography>

                <Switch
                  checked={isSandbox}
                  onChange={handleToggle}
                  size='small'
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: styles.text
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: styles.text
                    }
                  }}
                />
              </Stack>

              <Typography fontSize={11} color='text.secondary' mt={-0.5}>
                Safe test before going live
              </Typography>
            </Stack>
          )}
        </Box>
      </Box>

      <Dialog
        open={openModal}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setOpenModal(false);
        }}
        maxWidth='sm'
        slotProps={{
          paper: { sx: { borderRadius: 1 } }
        }}
      >
        <DialogContent sx={{ p: 2 }}>
          <Stack alignItems='center' spacing={2}>
            <Typography
              position='absolute'
              width='90%'
              textAlign='end'
              sx={{ cursor: 'pointer' }}
              onClick={() => setOpenModal(false)}
            >
              <RxCross1 />
            </Typography>

            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: '#FFE4E3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <WarningIcon />
            </Box>

            <Typography variant='h5' fontSize={20} fontWeight={500}>
              {hasConfigIssues
                ? 'Configuration Required'
                : targetMode === 'live'
                  ? 'Switch to Live Mode?'
                  : 'Switch to Sandbox Mode?'}
            </Typography>

            <Typography width='430px' fontSize={14} color='text.secondary' textAlign='center'>
              {hasConfigIssues
                ? 'Your agent needs to be properly configured before switching to live mode.'
                : targetMode === 'live'
                  ? "You're about to switch this agent from Sandbox Mode to Live Mode."
                  : 'The agent will run in a safe test environment with no live impact.'}
            </Typography>

            {hasConfigIssues && (
              <Stack width='100%' spacing={1} mt={2}>
                {agentStatus?.errors?.map((issue, index) => {
                  const [key, value] = Object.entries(issue)[0];

                  const icon = key === 'integration' ? <ModeToggleSettingIcon /> : <ConfigureActionIcon />;

                  return (
                    <Stack
                      key={index}
                      direction='row'
                      gap={1}
                      alignItems='center'
                      px={2}
                      py={0.75}
                      borderRadius='6px'
                      bgcolor='#FFF2F2'
                      color='#DA0808'
                    >
                      {icon}
                      <Typography color='error' fontSize={14} noWrap>
                        {`Configure => Actions: ${String(value)}`}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            )}

            <Stack direction='row' spacing={2} width='100%' justifyContent='end' mt={3}>
              <Button
                variant='outlined'
                onClick={() => setOpenModal(false)}
                sx={{
                  borderColor: theme.palette.grey[300],
                  '&:hover': {
                    borderColor: theme.palette.grey[400],
                    bgcolor: theme.palette.grey[50]
                  }
                }}
              >
                Cancel
              </Button>

              {hasConfigIssues ? (
                <Button variant='contained' onClick={handleConfigureNow}>
                  Configure Now
                </Button>
              ) : (
                <Button variant='contained' isPending={isPending} onClick={handleConfirmSwitch}>
                  {targetMode === 'live' ? 'Switch to Live Mode' : 'Switch to Sandbox Mode'}
                </Button>
              )}
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
